import { useEffect, useRef, useState } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { searchFilms } from '@/lib/catalog';

const NAV_LINKS = [
  { to: '/', label: '首页', end: true },
  { to: '/category', label: '影视作品', end: false },
  { to: '/news', label: '资讯', end: false },
  { to: '/about', label: '关于', end: false },
];

export default function Navbar() {
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [keyword, setKeyword] = useState('');
  const [focused, setFocused] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) {
        setFocused(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const results = searchFilms(keyword).slice(0, 6);
  const showResults = focused && keyword.trim().length > 0;

  const goDetail = (id: string) => {
    setKeyword('');
    setFocused(false);
    setMenuOpen(false);
    navigate(`/detail/${id}`);
  };

  return (
    <header
      className={`sticky top-0 z-50 w-full transition-colors duration-300 ${
        scrolled
          ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200'
          : 'bg-background-50'
      }`}
    >
      <div className="flex items-center justify-between h-16 md:h-20 px-4 md:px-6">
        <Link to="/" className="flex items-center gap-2.5 shrink-0">
          <span className="w-9 h-9 rounded-md bg-primary-500 text-background-50 flex items-center justify-center text-lg">
            <i className="ri-film-line"></i>
          </span>
          <span className="flex flex-col leading-none">
            <span className="font-heading font-bold text-foreground-950 text-base md:text-lg">
              神宫寺奈绪
            </span>
            <span className="text-[10px] md:text-xs text-foreground-500 tracking-widest">
              影视内容站
            </span>
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          {NAV_LINKS.map((link) => (
            <NavLink
              key={link.to}
              to={link.to}
              end={link.end}
              className={({ isActive }) =>
                `text-sm transition-colors ${
                  isActive
                    ? 'text-accent-600 font-medium'
                    : 'text-foreground-700 hover:text-accent-600'
                }`
              }
            >
              {link.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <div ref={boxRef} className="relative hidden md:block">
            <div className="flex items-center gap-2 bg-background-100 border border-background-200 rounded-md px-3 h-10 w-56 focus-within:border-accent-400 transition-colors">
              <i className="ri-search-line text-foreground-400"></i>
              <input
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                onFocus={() => setFocused(true)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && results[0]) goDetail(results[0].id);
                }}
                placeholder="搜索影视作品"
                className="bg-transparent border-none outline-none text-sm w-full text-foreground-900 placeholder:text-foreground-400"
              />
            </div>
            {showResults && (
              <div className="absolute right-0 mt-2 w-72 bg-background-50 border border-background-200 rounded-lg overflow-hidden">
                {results.length > 0 ? (
                  results.map((f) => (
                    <button
                      key={f.id}
                      onClick={() => goDetail(f.id)}
                      className="w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-background-100 transition-colors cursor-pointer"
                    >
                      <span className="w-9 h-12 rounded bg-background-200 overflow-hidden shrink-0">
                        <img src={f.cover} alt={f.title} className="w-full h-full object-top object-cover" />
                      </span>
                      <span className="min-w-0">
                        <span className="block text-sm text-foreground-900 truncate">{f.title}</span>
                        <span className="block text-xs text-foreground-500">
                          {f.type} · {f.year}
                        </span>
                      </span>
                    </button>
                  ))
                ) : (
                  <div className="px-3 py-4 text-sm text-foreground-500 text-center">没有找到相关作品</div>
                )}
              </div>
            )}
          </div>

          <button
            onClick={() => setMenuOpen((v) => !v)}
            className="md:hidden w-10 h-10 flex items-center justify-center text-foreground-800 text-xl cursor-pointer"
            aria-label="菜单"
          >
            <i className={menuOpen ? 'ri-close-line' : 'ri-menu-line'}></i>
          </button>
        </div>
      </div>

      {menuOpen && (
        <div className="md:hidden border-t border-background-200 bg-background-50 px-4 py-4">
          <div className="flex items-center gap-2 bg-background-100 border border-background-200 rounded-md px-3 h-10 mb-4">
            <i className="ri-search-line text-foreground-400"></i>
            <input
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索影视作品"
              className="bg-transparent border-none outline-none text-sm w-full text-foreground-900 placeholder:text-foreground-400"
            />
          </div>
          {keyword.trim() && (
            <div className="mb-4">
              {results.length > 0 ? (
                results.map((f) => (
                  <button
                    key={f.id}
                    onClick={() => goDetail(f.id)}
                    className="w-full flex items-center justify-between py-2 text-left text-sm text-foreground-800 cursor-pointer"
                  >
                    <span>{f.title}</span>
                    <span className="text-xs text-foreground-400">
                      {f.type} · {f.year}
                    </span>
                  </button>
                ))
              ) : (
                <div className="py-2 text-sm text-foreground-500">没有找到相关作品</div>
              )}
            </div>
          )}
          <nav className="flex flex-col">
            {NAV_LINKS.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                end={link.end}
                onClick={() => setMenuOpen(false)}
                className={({ isActive }) =>
                  `py-3 border-b border-background-100 text-sm ${
                    isActive ? 'text-accent-600 font-medium' : 'text-foreground-800'
                  }`
                }
              >
                {link.label}
              </NavLink>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
}